import { match } from 'ts-pattern';

type SignDisplay = 'always' | 'auto';
type Notation = 'compact' | 'standard';

type FormatNumberOptions = {
  applySignDisplay?: typeof _applySignDisplay;
  compactNumberFormatter?: typeof _compactNumberFormatter;
  decimals?: number;
  infinityValue?: string;
  locale?: string;
  notation?: Notation;
  signDisplay?: SignDisplay;
  undefinedValue?: string;
  useGrouping?: boolean;
};

/**
 * Applies sign display rules to a formatted number string.
 * Necessary as ios does not support "always" signed
 * @param value - The original number value
 * @param formatted - The formatted number string (without sign)
 * @param signDisplay - Whether to always show the sign
 * @returns The formatted string with appropriate sign
 */
const _applySignDisplay = (value: number, formatted: string, signDisplay: SignDisplay): string => {
  if (Object.is(value, -0)) {
    return `-${formatted}`;
  }
  if (value < 0) {
    return `-${formatted}`;
  }
  if (signDisplay === 'always' && value >= 0) {
    return `+${formatted}`;
  }
  return formatted;
};

/**
 * Formats a number using compact notation (K, M, B).
 * Necessary as ios does not support "compact" notation
 * @param value - The number to format
 * @param signDisplay - Whether to always show the sign
 * @param decimals - Number of decimal places to show
 * @param applySignDisplay - Function to handle sign display
 * @returns The formatted string
 */

const _compactNumberFormatter = (
  value: number,
  signDisplay: SignDisplay,
  decimals: number | undefined,
  applySignDisplay: typeof _applySignDisplay = _applySignDisplay,
): string => {
  const absValue = Math.abs(value);

  const formatWithDecimals = (v: number, divisor: number, suffix: string) => {
    const divided = v / divisor;
    const hasSignificantDecimals = divided % 1 !== 0;
    const decimalPlaces = decimals ?? (hasSignificantDecimals ? 1 : 0);
    return `${divided.toFixed(decimalPlaces)}${suffix}`;
  };

  const formatted = match(absValue)
    .when(
      (v) => v >= 1e9,
      (v) => formatWithDecimals(v, 1e9, 'B'),
    )
    .when(
      (v) => v >= 1e6,
      (v) => formatWithDecimals(v, 1e6, 'M'),
    )
    .when(
      (v) => v >= 1e3,
      (v) => formatWithDecimals(v, 1e3, 'K'),
    )
    .otherwise(() => absValue.toString());
    
  return applySignDisplay(value, formatted, signDisplay);
};


/**
 * Formats a number according to the specified options.
 * This utility handles various number formatting scenarios including:
 * - Standard and compact notation (K, M, B)
 * - Custom decimal places
 * - Sign display (always/auto)
 * - Locale-specific formatting
 * - Special cases (undefined, NaN, Infinity)
 *
 * @param value - The number to format. Can be undefined.
 * @param options - Formatting options
 * @param options.applySignDisplay - Custom function to handle sign display (defaults to internal implementation)
 * @param options.compactNumberFormatter - Custom formatter for compact notation (defaults to internal implementation)
 * @param options.decimals - Number of decimal places to show. If not specified, uses 1 decimal for compact notation when needed
 * @param options.locale - Locale to use for formatting (defaults to navigator.language or 'en-US')
 * @param options.notation - Number notation style ('standard' or 'compact')
 * @param options.signDisplay - Whether to always show the sign ('always' or 'auto')
 * @param options.useGrouping - Whether to use thousand separators (defaults to true)
 * @param options.undefinedValue - String to display for undefined/NaN values (defaults to '-')
 * @param options.infinityValue - String to display for infinity (defaults to '∞')
 *
 * @returns The formatted string. Returns '-' for undefined/NaN values, '∞' or '-∞' for infinite values
 *
 * @example
 * // Basic usage
 * formatNumber(1234) // "1,234"
 *
 * // Compact notation
 * formatNumber(1234, { notation: 'compact' }) // "1.2K"
 * formatNumber(1234567, { notation: 'compact' }) // "1.2M"
 *
 * // Decimal places
 * formatNumber(1234.5678, { decimals: 2 }) // "1,234.57"
 *
 * // Sign display
 * formatNumber(1234, { signDisplay: 'always' }) // "+1,234"
 * formatNumber(-1234, { signDisplay: 'always' }) // "-1,234"
 *
 * // Locale-specific
 * formatNumber(1234.56, { locale: 'de-DE' }) // "1.234,56"
 *
 * // Special cases
 * formatNumber(undefined) // "-"
 * formatNumber(NaN) // "-"
 * formatNumber(Infinity) // "∞"
 * formatNumber(-Infinity) // "-∞"
 *
 * // Custom special case values
 * formatNumber(undefined, { undefinedValue: 'N/A' }) // "N/A"
 * formatNumber(Infinity, { infinityValue: '∞∞' }) // "∞∞"
 * formatNumber(-Infinity, { infinityValue: '∞∞' }) // "-∞∞"
 */
export const formatNumber = (
  value: number | undefined,
  {
    applySignDisplay = _applySignDisplay,
    compactNumberFormatter = _compactNumberFormatter,
    decimals,
    infinityValue = '∞',
    locale = navigator.language ?? 'en-US',
    notation = 'standard',
    signDisplay = 'auto',
    undefinedValue = '-',
    useGrouping = true,
  }: FormatNumberOptions = {},
): string => {
  if (value === undefined || Number.isNaN(value)) {
    return undefinedValue;
  }
  if (!Number.isFinite(value)) {
    return applySignDisplay(value, infinityValue, signDisplay);
  }

  if (notation === 'compact' && compactNumberFormatter) {
    return compactNumberFormatter(value, signDisplay, decimals, applySignDisplay);
  }

  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    notation,
    useGrouping,
  }).format(Math.abs(value));

  return applySignDisplay(value, formatted, signDisplay);
};
